/**
 * OKRs Page — /prioritization/okrs
 *
 * Full OKR management: list objectives, expand key results,
 * create/edit/delete OKRs and key results, log check-ins.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Target,
  Plus,
  ChevronDown,
  ChevronRight,
  Trash2,
  TrendingUp,
  CheckCircle2,
  AlertTriangle,
  Clock,
  X,
  BarChart3,
  Layers,
  Flag,
} from 'lucide-react';
import { PageHeader } from '../../components/prioritization/PageHeader';
import { useOKRStore } from '../../stores/prioritization/okrStore';
import {
  keyResultProgress,
  okrProgress,
  OKR_STATUS_LABELS,
  OKR_STATUS_COLORS,
  KR_STATUS_COLORS,
  KR_CONFIDENCE_COLORS,
  type OKR,
  type OKRKeyResult,
  type OKRStatus,
  type KeyResultStatus,
  type KeyResultConfidence,
  type KeyResultMetricType,
  type OKRTimePeriod,
} from '../../types/okr';

// ── Status icon helper ────────────────────────────────────

function StatusIcon({ status }: { status: OKRStatus | KeyResultStatus }) {
  if (status === 'completed') return <CheckCircle2 className="h-4 w-4 text-[var(--text-ter)]" />;
  if (status === 'at_risk')   return <AlertTriangle className="h-4 w-4 text-[var(--mustard)]" />;
  if (status === 'behind')    return <AlertTriangle className="h-4 w-4 text-[var(--coral)]" />;
  if (status === 'active' || status === 'on_track') return <TrendingUp className="h-4 w-4 text-[var(--green)]" />;
  return <Clock className="h-4 w-4 text-[var(--text-ter)]" />;
}

// ── Progress bar ──────────────────────────────────────────

function ProgressBar({ value, status }: { value: number; status: OKRStatus | KeyResultStatus }) {
  const color =
    status === 'completed' ? 'bg-[var(--slate)]' :
    status === 'at_risk'   ? 'bg-[var(--mustard)]' :
    status === 'behind'    ? 'bg-[var(--coral)]' :
    (status === 'active' || status === 'on_track') ? 'bg-[var(--green)]' :
    'bg-[var(--rule-bright)]';

  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-[var(--hover-tint)] rounded-full h-2 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-300 ${color}`}
          style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
        />
      </div>
      <span className="text-xs font-medium text-[var(--text-sec)] w-8 text-right">{value}%</span>
    </div>
  );
}

// ── Inline editable field ─────────────────────────────────

function InlineField({
  label,
  value,
  onSave,
  type = 'text',
  placeholder,
}: {
  label: string;
  value: string;
  onSave: (val: string) => void;
  type?: string;
  placeholder?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  const commit = () => {
    setEditing(false);
    if (draft !== value) onSave(draft);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <input
          autoFocus
          type={type}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setEditing(false); setDraft(value); } }}
          className="border border-[var(--slate)] rounded px-2 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          placeholder={placeholder}
        />
      </div>
    );
  }

  return (
    <button
      onClick={() => { setDraft(value); setEditing(true); }}
      className="text-sm text-[var(--text-sec)] hover:text-[var(--text-ter)] hover:underline text-left truncate max-w-xs"
      title={`Edit ${label}`}
    >
      {value || <span className="text-[var(--text-ter)] italic">{placeholder ?? `Add ${label}`}</span>}
    </button>
  );
}

// ── Create OKR Modal ──────────────────────────────────────

interface CreateOKRModalProps {
  onClose: () => void;
  onCreate: (input: { title: string; timePeriod: OKRTimePeriod; periodLabel: string; status: OKRStatus }) => void;
}

function CreateOKRModal({ onClose, onCreate }: CreateOKRModalProps) {
  const [title, setTitle] = useState('');
  const [timePeriod, setTimePeriod] = useState<OKRTimePeriod>('quarterly');
  const [periodLabel, setPeriodLabel] = useState('');
  const [status, setStatus] = useState<OKRStatus>('active');

  const submit = () => {
    if (!title.trim()) return;
    onCreate({ title: title.trim(), timePeriod, periodLabel, status });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-[var(--bg-raised)] shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--rule)]">
          <h2 className="text-lg font-semibold text-[var(--text-pri)]">New Objective</h2>
          <button onClick={onClose} className="text-[var(--text-ter)] hover:text-[var(--text-sec)]"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Objective *</label>
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submit()}
              className="w-full border border-[var(--rule)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. Delight customers with faster shipping"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Time period</label>
              <select
                value={timePeriod}
                onChange={(e) => setTimePeriod(e.target.value as OKRTimePeriod)}
                className="w-full border border-[var(--rule)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="annual">Annual</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Period label</label>
              <input
                value={periodLabel}
                onChange={(e) => setPeriodLabel(e.target.value)}
                className="w-full border border-[var(--rule)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. Q3 2025"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as OKRStatus)}
              className="w-full border border-[var(--rule)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="draft">Draft</option>
              <option value="active">Active</option>
            </select>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[var(--rule)] flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)] hover:bg-[var(--surface)]">Cancel</button>
          <button
            onClick={submit}
            disabled={!title.trim()}
            className="px-4 py-2 text-sm bg-[var(--slate)] hover:bg-[var(--slate)] text-white disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Create objective
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Add Key Result Modal ──────────────────────────────────

interface AddKRModalProps {
  okrTitle: string;
  onClose: () => void;
  onAdd: (input: {
    title: string;
    metricType: KeyResultMetricType;
    metricUnit: string;
    startValue: number;
    targetValue: number;
    confidence: KeyResultConfidence;
  }) => void;
}

function AddKRModal({ okrTitle, onClose, onAdd }: AddKRModalProps) {
  const [title, setTitle] = useState('');
  const [metricType, setMetricType] = useState<KeyResultMetricType>('percentage');
  const [metricUnit, setMetricUnit] = useState('');
  const [startValue, setStartValue] = useState(0);
  const [targetValue, setTargetValue] = useState(100);
  const [confidence, setConfidence] = useState<KeyResultConfidence>('medium');

  const submit = () => {
    if (!title.trim()) return;
    onAdd({ title: title.trim(), metricType, metricUnit, startValue, targetValue, confidence });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-[var(--bg-raised)] shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--rule)]">
          <div>
            <h2 className="text-lg font-semibold text-[var(--text-pri)]">Add Key Result</h2>
            <p className="text-sm text-[var(--text-ter)] truncate max-w-xs">{okrTitle}</p>
          </div>
          <button onClick={onClose} className="text-[var(--text-ter)] hover:text-[var(--text-sec)]"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Key result *</label>
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border border-[var(--rule)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. Reduce delivery time to under 2 days"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Metric type</label>
              <select
                value={metricType}
                onChange={(e) => setMetricType(e.target.value as KeyResultMetricType)}
                className="w-full border border-[var(--rule)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="percentage">Percentage (%)</option>
                <option value="number">Number</option>
                <option value="currency">Currency</option>
                <option value="boolean">Achieved / Not achieved</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Unit (optional)</label>
              <input
                value={metricUnit}
                onChange={(e) => setMetricUnit(e.target.value)}
                className="w-full border border-[var(--rule)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. days, users, $"
              />
            </div>
          </div>
          {metricType !== 'boolean' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Start value</label>
                <input
                  type="number"
                  value={startValue}
                  onChange={(e) => setStartValue(Number(e.target.value))}
                  className="w-full border border-[var(--rule)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Target value</label>
                <input
                  type="number"
                  value={targetValue}
                  onChange={(e) => setTargetValue(Number(e.target.value))}
                  className="w-full border border-[var(--rule)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Confidence</label>
            <div className="flex gap-2">
              {(['high', 'medium', 'low'] as KeyResultConfidence[]).map((c) => (
                <button
                  key={c}
                  onClick={() => setConfidence(c)}
                  className={`flex-1 py-1.5 text-sm  border capitalize transition-colors ${
                    confidence === c
                      ? c === 'high' ? 'bg-[var(--green-dim)] border-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)] font-medium'
                      : c === 'medium' ? 'bg-[var(--mustard-dim)] border-[var(--mustard)] text-[var(--mustard)] font-medium'
                      : 'bg-[var(--coral-dim)] border-[var(--coral)] text-[var(--coral)] font-medium'
                      : 'border-[var(--rule)] text-[var(--text-ter)] hover:border-[var(--rule)]'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[var(--rule)] flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)] hover:bg-[var(--surface)]">Cancel</button>
          <button
            onClick={submit}
            disabled={!title.trim()}
            className="px-4 py-2 text-sm bg-[var(--slate)] hover:bg-[var(--slate)] text-white disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Add key result
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Check-in Modal ────────────────────────────────────────

interface CheckInModalProps {
  keyResult: OKRKeyResult;
  onClose: () => void;
  onSubmit: (value: number, note: string) => void;
}

function CheckInModal({ keyResult, onClose, onSubmit }: CheckInModalProps) {
  const [value, setValue] = useState(keyResult.currentValue);
  const [note, setNote] = useState('');

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-[var(--bg-raised)] shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--rule)]">
          <div>
            <h2 className="text-lg font-semibold text-[var(--text-pri)]">Log progress</h2>
            <p className="text-sm text-[var(--text-ter)] truncate max-w-xs">{keyResult.title}</p>
          </div>
          <button onClick={onClose} className="text-[var(--text-ter)] hover:text-[var(--text-sec)]"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Current value {keyResult.metricUnit ? `(${keyResult.metricUnit})` : ''}
            </label>
            <input
              autoFocus
              type="number"
              value={value}
              onChange={(e) => setValue(Number(e.target.value))}
              className="w-full border border-[var(--rule)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-[var(--text-ter)] mt-1">Target: {keyResult.targetValue}{keyResult.metricUnit ? ` ${keyResult.metricUnit}` : ''}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Note (optional)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              className="w-full border border-[var(--rule)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              placeholder="What happened? Any blockers?"
            />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[var(--rule)] flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)] hover:bg-[var(--surface)]">Cancel</button>
          <button
            onClick={() => { onSubmit(value, note); onClose(); }}
            className="px-4 py-2 text-sm bg-[var(--slate)] hover:bg-[var(--slate)] text-white"
          >
            Save check-in
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Key Result row ────────────────────────────────────────

function KeyResultRow({ kr, onUpdate, onDelete, onCheckIn }: {
  kr: OKRKeyResult;
  onUpdate: (id: string, field: string, value: unknown) => void;
  onDelete: (id: string) => void;
  onCheckIn: (kr: OKRKeyResult) => void;
}) {
  const progress = keyResultProgress(kr);
  const krColors = KR_STATUS_COLORS[kr.status] ?? KR_STATUS_COLORS.not_started;
  const confColor = KR_CONFIDENCE_COLORS[kr.confidence] ?? '';

  const formatValue = (v: number) => {
    if (kr.metricType === 'currency') return `$${v.toLocaleString()}`;
    if (kr.metricType === 'boolean') return v ? 'Done' : 'Not done';
    return `${v}${kr.metricUnit ? ` ${kr.metricUnit}` : ''}`;
  };

  return (
    <div className="flex items-start gap-3 py-3 px-4 hover:bg-[var(--surface)] group transition-colors">
      <div className="mt-0.5">
        <StatusIcon status={kr.status} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-sm font-medium text-[var(--text-pri)] truncate">{kr.title}</span>
          <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${krColors.bg} ${krColors.text}`}>
            {kr.status.replace('_', ' ')}
          </span>
          <span className={`text-xs font-medium capitalize ${confColor}`}>{kr.confidence}</span>
        </div>
        <div className="mb-1">
          <ProgressBar value={progress} status={kr.status} />
        </div>
        <div className="flex items-center gap-3 text-xs text-[var(--text-ter)]">
          <span>{formatValue(kr.currentValue)} of {formatValue(kr.targetValue)}</span>
        </div>
      </div>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={() => onCheckIn(kr)}
          className="p-1.5 text-[var(--text-ter)] hover:text-[var(--text-ter)] hover:bg-[var(--slate-dim)] rounded"
          title="Log progress"
        >
          <TrendingUp className="h-3.5 w-3.5" />
        </button>
        <select
          value={kr.status}
          onChange={(e) => onUpdate(kr.id, 'status', e.target.value)}
          className="text-xs border border-[var(--rule)] rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-400"
          title="Change status"
        >
          <option value="not_started">Not started</option>
          <option value="on_track">On track</option>
          <option value="at_risk">At risk</option>
          <option value="behind">Behind</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <button
          onClick={() => onDelete(kr.id)}
          className="p-1.5 text-[var(--text-ter)] hover:text-[var(--coral)] hover:bg-[var(--coral-dim)] rounded"
          title="Delete"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

// ── OKR card ──────────────────────────────────────────────

function OKRCard({ okr, keyResults, onUpdate, onDelete, onAddKR, onUpdateKR, onDeleteKR, onCheckIn }: {
  okr: OKR;
  keyResults: OKRKeyResult[];
  onUpdate: (id: string, updates: Record<string, unknown>) => void;
  onDelete: (id: string) => void;
  onAddKR: (okr: OKR) => void;
  onUpdateKR: (id: string, field: string, value: unknown) => void;
  onDeleteKR: (id: string) => void;
  onCheckIn: (kr: OKRKeyResult) => void;
}) {
  const [expanded, setExpanded] = useState(true);
  const progress = okrProgress({ ...okr, keyResults });
  const statusColors = OKR_STATUS_COLORS[okr.status] ?? OKR_STATUS_COLORS.draft;

  return (
    <div className={`bg-[var(--bg-raised)]  border-2 ${statusColors.border} shadow-sm overflow-hidden`}>
      {/* Header */}
      <div className="px-5 py-4">
        <div className="flex items-start gap-3">
          <button
            onClick={() => setExpanded(!expanded)}
            className="mt-0.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] flex-shrink-0"
          >
            {expanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-4 mb-2">
              <div className="flex-1">
                <InlineField
                  label="title"
                  value={okr.title}
                  onSave={(v) => onUpdate(okr.id, { title: v })}
                  placeholder="Objective title"
                />
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {okr.periodLabel && (
                  <span className="text-xs text-[var(--text-ter)] bg-[var(--hover-tint)] px-2 py-0.5 rounded-full">
                    {okr.periodLabel}
                  </span>
                )}
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColors.bg} ${statusColors.text}`}>
                  {OKR_STATUS_LABELS[okr.status]}
                </span>
                <select
                  value={okr.status}
                  onChange={(e) => onUpdate(okr.id, { status: e.target.value as OKRStatus })}
                  className="text-xs border border-[var(--rule)] rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-400 hidden group-hover:block"
                  title="Change status"
                />
                <button
                  onClick={() => onDelete(okr.id)}
                  className="p-1 text-[var(--text-ter)] hover:text-[var(--coral)] hover:bg-[var(--coral-dim)] rounded transition-colors"
                  title="Delete objective"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="mb-3">
              <ProgressBar value={progress} status={okr.status} />
            </div>
            <div className="flex items-center gap-4 text-xs text-[var(--text-ter)]">
              <span className="flex items-center gap-1">
                <Flag className="h-3.5 w-3.5" />
                {keyResults.length} key result{keyResults.length !== 1 ? 's' : ''}
              </span>
              <span className="capitalize">{okr.timePeriod}</span>
              {okr.status !== 'completed' && okr.status !== 'cancelled' && (
                <button
                  onClick={() => onUpdate(okr.id, { status: 'active' as OKRStatus })}
                  className="text-[var(--text-ter)] hover:text-[var(--text-ter)]"
                  style={{ display: okr.status === 'draft' ? 'inline' : 'none' }}
                >
                  Activate
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Key Results */}
      {expanded && (
        <div className="border-t border-[var(--rule)]">
          {keyResults.length > 0 ? (
            <div className="divide-y divide-[var(--rule)]">
              {keyResults.map((kr) => (
                <KeyResultRow
                  key={kr.id}
                  kr={kr}
                  onUpdate={onUpdateKR}
                  onDelete={onDeleteKR}
                  onCheckIn={onCheckIn}
                />
              ))}
            </div>
          ) : (
            <div className="px-5 py-4 text-sm text-[var(--text-ter)] italic">No key results yet</div>
          )}
          <div className="px-5 py-3 border-t border-[var(--rule)]">
            <button
              onClick={() => onAddKR(okr)}
              className="flex items-center gap-1.5 text-sm text-[var(--text-ter)] hover:text-[var(--text-ter)] font-medium"
            >
              <Plus className="h-4 w-4" />
              Add key result
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Summary stats ─────────────────────────────────────────

function SummaryStats({ okrs, keyResults }: { okrs: OKR[]; keyResults: OKRKeyResult[] }) {
  const active    = okrs.filter((o) => o.status === 'active').length;
  const atRisk    = okrs.filter((o) => o.status === 'at_risk' || o.status === 'behind').length;
  const completed = okrs.filter((o) => o.status === 'completed').length;
  const totalKRs  = keyResults.length;
  const doneKRs   = keyResults.filter((kr) => kr.status === 'completed').length;

  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
      {[
        { label: 'Total OKRs',     value: okrs.length,   icon: Layers,       color: 'text-[var(--text-ter)]' },
        { label: 'Active',         value: active,         icon: TrendingUp,   color: 'text-[var(--green)]' },
        { label: 'At risk / behind', value: atRisk,       icon: AlertTriangle, color: 'text-[var(--mustard)]' },
        { label: 'Completed',      value: completed,      icon: CheckCircle2, color: 'text-[var(--text-ter)]' },
        { label: 'KR completion',  value: `${totalKRs ? Math.round((doneKRs / totalKRs) * 100) : 0}%`, icon: BarChart3, color: 'text-[var(--text-sec)]' },
      ].map(({ label, value, icon: Icon, color }) => (
        <div key={label} className="bg-[var(--bg-raised)] border border-[var(--rule)] px-4 py-3 flex items-center gap-3">
          <Icon className={`h-5 w-5 flex-shrink-0 ${color}`} />
          <div>
            <div className="text-xl font-bold text-[var(--text-pri)]">{value}</div>
            <div className="text-xs text-[var(--text-ter)]">{label}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────

export default function OKRsPage() {
  const {
    okrs, keyResults, isLoading, error,
    fetchOKRs, fetchKeyResults,
    createOKR, updateOKR, deleteOKR,
    createKeyResult, updateKeyResult, deleteKeyResult,
    addCheckIn,
    getTopLevelOKRs, getKeyResultsForOKR,
  } = useOKRStore();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [addKRTarget, setAddKRTarget] = useState<OKR | null>(null);
  const [checkInTarget, setCheckInTarget] = useState<OKRKeyResult | null>(null);
  const [statusFilter, setStatusFilter] = useState<OKRStatus | 'all'>('all');

  useEffect(() => {
    fetchOKRs();
  }, [fetchOKRs]);

  // Fetch key results for all loaded OKRs — keyed on stable ID set
  const okrIdKey = okrs.map((o) => o.id).join(',');
  useEffect(() => {
    okrs.forEach((o) => fetchKeyResults(o.id));
  }, [okrIdKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateOKR = useCallback(async (input: Parameters<typeof createOKR>[0]) => {
    await createOKR(input);
  }, [createOKR]);

  const handleUpdateOKR = useCallback(async (id: string, updates: Record<string, unknown>) => {
    await updateOKR(id, updates as Parameters<typeof updateOKR>[1]);
  }, [updateOKR]);

  const handleDeleteOKR = useCallback(async (id: string) => {
    if (!confirm('Delete this objective and all its key results?')) return;
    await deleteOKR(id);
  }, [deleteOKR]);

  const handleAddKR = useCallback(async (okr: OKR, input: Parameters<typeof createKeyResult>[1]) => {
    await createKeyResult(okr.id, input);
  }, [createKeyResult]);

  const handleUpdateKR = useCallback(async (id: string, field: string, value: unknown) => {
    await updateKeyResult(id, { [field]: value } as Parameters<typeof updateKeyResult>[1]);
  }, [updateKeyResult]);

  const handleDeleteKR = useCallback(async (id: string) => {
    if (!confirm('Delete this key result?')) return;
    await deleteKeyResult(id);
  }, [deleteKeyResult]);

  const handleCheckIn = useCallback(async (kr: OKRKeyResult, value: number, note: string) => {
    await addCheckIn(kr.id, value, note || undefined);
  }, [addCheckIn]);

  const topLevel = getTopLevelOKRs();
  const filtered = statusFilter === 'all' ? topLevel : topLevel.filter((o) => o.status === statusFilter);

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="OKRs"
        subtitle="Objectives & Key Results — set ambitious goals and track measurable outcomes"
        icon={<Target className="h-6 w-6" />}
        actions={
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--slate)] hover:bg-[var(--slate)] text-white text-sm font-medium transition-colors"
          >
            <Plus className="h-4 w-4" />
            New objective
          </button>
        }
      />

      <div className="flex-1 overflow-y-auto px-6 py-6">
        {/* Summary stats */}
        <SummaryStats okrs={okrs} keyResults={keyResults} />

        {/* Filters */}
        <div className="flex items-center gap-2 mb-5">
          {(['all', 'active', 'at_risk', 'behind', 'completed', 'draft'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setStatusFilter(f)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors capitalize ${
                statusFilter === f
                  ? 'bg-[var(--slate)] text-white border-[var(--slate)]'
                  : 'bg-[var(--bg-raised)] text-[var(--text-sec)] border-[var(--rule)] hover:border-[var(--rule)]'
              }`}
            >
              {f === 'all' ? 'All' : f.replace('_', ' ')}
            </button>
          ))}
        </div>

        {/* Content */}
        {isLoading && (
          <div className="flex items-center justify-center py-20 text-[var(--text-ter)]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--slate)] mx-auto mb-3" />
              <p className="text-sm">Loading OKRs…</p>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-[var(--coral-dim)] border border-[var(--coral)] text-[var(--coral)] px-4 py-3 text-sm mb-4">
            {error}
          </div>
        )}

        {!isLoading && filtered.length === 0 && (
          <div className="text-center py-20">
            <Target className="h-12 w-12 text-[var(--text-ter)] mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[var(--text-sec)] mb-2">
              {statusFilter === 'all' ? 'No objectives yet' : `No ${statusFilter.replace('_', ' ')} objectives`}
            </h3>
            <p className="text-sm text-[var(--text-ter)] mb-6 max-w-md mx-auto">
              OKRs help teams align on what matters most. Create your first objective and add
              key results to measure progress.
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--slate)] hover:bg-[var(--slate)] text-white text-sm font-medium"
            >
              <Plus className="h-4 w-4" />
              Create first objective
            </button>
          </div>
        )}

        <div className="space-y-4">
          {filtered.map((okr) => (
            <OKRCard
              key={okr.id}
              okr={okr}
              keyResults={getKeyResultsForOKR(okr.id)}
              onUpdate={handleUpdateOKR}
              onDelete={handleDeleteOKR}
              onAddKR={setAddKRTarget}
              onUpdateKR={handleUpdateKR}
              onDeleteKR={handleDeleteKR}
              onCheckIn={setCheckInTarget}
            />
          ))}
        </div>
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateOKRModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateOKR}
        />
      )}

      {addKRTarget && (
        <AddKRModal
          okrTitle={addKRTarget.title}
          onClose={() => setAddKRTarget(null)}
          onAdd={(input) => handleAddKR(addKRTarget, input)}
        />
      )}

      {checkInTarget && (
        <CheckInModal
          keyResult={checkInTarget}
          onClose={() => setCheckInTarget(null)}
          onSubmit={(value, note) => handleCheckIn(checkInTarget, value, note)}
        />
      )}
    </div>
  );
}
